import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserCircleIcon } from '@heroicons/react/24/outline';
import DashboardHeader from '../../../shared/components/DashboardHeader';
import LoginDetailsForm, { MustChangeLoginNotice } from '../../../shared/components/LoginDetailsForm';
import { getStrandLabel } from '../../../shared/constant/strands';
import { useAuth } from '../../../core/providers/AuthProvider';
import { getOwnStudent, updateOwnLogin } from '../../StudentRecord/services/studentRecordService';

function ReadOnlyField({ label, value }) {
  return (
    <div className="space-y-0.5">
      <p className="text-sm font-medium uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-lg font-bold text-gray-800">{value || '—'}</p>
    </div>
  );
}

// The signed-in student's own profile. School records (LRN, name, grade, section, strand) are shown
// read-only since staff keep those; the student can only change their username and password. A
// student still on the login staff gave them is kept here (RoleGuard) until they choose their own.
function StudentProfilePage() {
  const navigate = useNavigate();
  const { mustChangeLogin, markLoginChanged } = useAuth();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let mounted = true;
    getOwnStudent()
      .then((row) => {
        if (!mounted) return;
        if (row) setStudent(row);
        else setLoadError('No student record is linked to this account.');
      })
      .catch((err) => {
        if (mounted) setLoadError(err.message || 'Failed to load your profile');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="h-dvh w-full flex flex-col bg-appbg overflow-hidden">
      <DashboardHeader title="My Profile" backTo={mustChangeLogin ? null : '/student-dashboard'} showStudentMenu />

      <div className="flex-1 min-h-0 overflow-y-auto">
        <main className="w-full p-4 lg:w-3/4 lg:mx-auto">
          {loading ? (
            <p className="w-full text-center text-gray-500 py-12">Loading…</p>
          ) : loadError ? (
            <p className="w-full text-center text-red-600 py-12">{loadError}</p>
          ) : (
            <div className="w-full space-y-4">
              {mustChangeLogin && <MustChangeLoginNotice />}

              <section className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
                <div className="flex flex-col items-center text-center border-b border-gray-200 pb-4">
                  <UserCircleIcon className="h-24 w-24 text-[#4ea6d0]" aria-hidden="true" />
                  <h2 className="mt-1 text-2xl font-semibold text-[#0b4a71] uppercase">
                    {[student.firstname, student.middlename, student.lastname].filter(Boolean).join(' ')}
                  </h2>
                  <p className="text-lg text-gray-600">LRN: {student.lrn}</p>
                </div>

                <h3 className="font-semibold text-lg text-[#0b4a71]">School Record</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <ReadOnlyField
                    label="Grade & Section"
                    value={`Grade ${student.grade_level}${student.section ? ` - ${student.section}` : ''}`}
                  />
                  <ReadOnlyField label="School Year" value={student.school_year} />
                  {student.strand && <ReadOnlyField label="Strand" value={getStrandLabel(student.strand)} />}
                  {student.track && <ReadOnlyField label="Track" value={student.track} />}
                </div>
                <p className="text-lg italic text-gray-500">
                  Something wrong here? Ask your adviser to correct it.
                </p>
              </section>

              <LoginDetailsForm
                username={student.username}
                mustChange={mustChangeLogin}
                save={updateOwnLogin}
                onSaved={(username) => {
                  setStudent((s) => ({ ...s, username }));
                  if (mustChangeLogin) {
                    markLoginChanged();
                    navigate('/student-dashboard', { replace: true });
                  }
                }}
              />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default StudentProfilePage;
